import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import PrivacyPolicy from '@/modules/auth/ui/LegalContracts/PrivacyPolicy';
import TermsServices from '@/modules/auth/ui/LegalContracts/TermsServices';
import Login from '@/modules/auth/ui/Login';
import Unauthorized from '@/modules/errors/ui/Unauthorized';
import ReportRedirect from '@/modules/reports/ReportRedirect';
import Root from '@/Root';
import ProtectedRoute from '@/router/ProtectedRoute';

const ParameterRedirect = lazy(() => import('@/modules/parameterManagement/ParameterRedirect'));
const CompanyParameter = lazy(() => import('@/modules/parameterManagement/CompanyParameter/ui'));
const ProductParameter = lazy(() => import('@/modules/parameterManagement/ProductParameter/ui'));
const LicansorParameter = lazy(() => import('@/modules/parameterManagement/LicansorParameter/ui'));
const ProducerParameter = lazy(() => import('@/modules/parameterManagement/ProducerParameter/ui'));
const ParameterManagement = lazy(() => import('@/modules/parameterManagement/ui'));
const CompanyReports = lazy(() => import('@/modules/reports/CompanyReports/ui'));
const PortalReports = lazy(() => import('@/modules/reports/PortalReports'));
const ReportLayer = lazy(() => import('@/modules/reports/_layout'));
const UserReports = lazy(() => import('@/modules/reports/UserReports'));
const ForgotPassword = lazy(() => import('@/modules/auth/ui/ForgotPassword'));
const IntroductionSteps = lazy(() => import('@/modules/auth/ui/Introduction'));
const ChannelUsers = lazy(() => import('@/modules/channel/channelUsers/ui/index'));
const LiveStream = lazy(() => import('@/modules/channel/liveStream/index'));
const ChannelContext = lazy(() => import('@/modules/channel/content/ui'));
const Playlist = lazy(() => import('@/modules/channel/contentPlaylist/ui'));
const PlaylistVideo = lazy(() => import('@/modules/channel/contentPlaylistVideo/ui'));
const Videos = lazy(() => import('@/modules/channel/contentVideo'));
const ShortVideos = lazy(() => import('@/modules/channel/shortVideo/ui'));
const Settings = lazy(() => import('@/modules/channel/settings/ui'));
const Channel = lazy(() => import('@/modules/channel/ui'));
const Cms = lazy(() => import('@/modules/cms/ui'));
const CmsChannels = lazy(() => import('@/modules/cmsManagement/cmsChannels/ui'));
const CmsPolicies = lazy(() => import('@/modules/cmsManagement/Policies/ui'));
const CmsSettings = lazy(() => import('@/modules/cmsManagement/Settings/ui'));
const CmsManagement = lazy(() => import('@/modules/cmsManagement/ui'));
const AuthenticatedNotFound = lazy(() => import('@/modules/errors/ui/AuthenticatedNotFound'));
const MyChannels = lazy(() => import('@/modules/myChannels/ui'));
const Organization = lazy(() => import('@/modules/organization/ui'));
const RoleManagement = lazy(() => import('@/modules/roleManagement/ui'));
const Documents = lazy(() => import('@/modules/statics/Documents'));
const Users = lazy(() => import('@/modules/users/ui'));
const ProfileLayer = lazy(() => import('@/modules/profile/_layout'));
const NotificationPreferences = lazy(() => import('@/modules/profile/NotificationPreferences/ui'));
const ProfileInformation = lazy(() => import('@/modules/profile/ProfileInformation'));
const DashboardPanel = lazy(() => import('@/modules/dashboard'));
const Notifications = lazy(() => import('@/modules/notifications'));
const FacebookPages = lazy(() => import('@/modules/facebookPages/ui'));
const FacebookPageDetail = lazy(() => import('@/modules/facebookPages/layout/subLayout'));
const FacebookPageContent = lazy(() => import('@/modules/facebookPages/layout'));
const FacebookPageVideos = lazy(() => import('@/modules/facebookPages/videos'));
const FacebookPageExpiredVideos = lazy(
  () => import('@/modules/facebookPages/layout/tabs/ExpiredVideos')
);
const FacebookPageUsers = lazy(() => import('@/modules/facebookPages/layout/tabs/Users'));
const FacebookPageSettings = lazy(() => import('@/modules/facebookPages/layout/tabs/Settings'));
const FacebookCms = lazy(() => import('@/modules/facebook-cms/ui'));

const InfoComponent = lazy(() => import('@/components/info/InfoComponent'));
const MyWorks = lazy(() => import('@/modules/profile/MyWork'));
const PerformanceReporting = lazy(() => import('@/modules/performanceReporting'));
const getChannelChildRoutes = () => [
  {
    index: true,
    element: <Navigate to="contents/videos" replace />,
  },
  {
    path: 'contents',
    element: <ChannelContext />,
    children: [
      {
        index: true,
        element: <Navigate to="videos" replace />,
      },
      {
        path: 'videos/*',
        element: (
          <ProtectedRoute permission={['video_read']}>
            <Videos />
          </ProtectedRoute>
        ),
      },
      {
        path: 'playlists/*',
        element: (
          <ProtectedRoute permission={['playlist_read']}>
            <Playlist />
          </ProtectedRoute>
        ),
      },
      {
        path: 'playlists/playlistvideo/*',
        element: (
          <ProtectedRoute permission={['playlist_read']}>
            <PlaylistVideo />
          </ProtectedRoute>
        ),
      },
      {
        path: 'shortvideos/*',
        element: (
          <ProtectedRoute permission={['video_read']}>
            <ShortVideos />
          </ProtectedRoute>
        ),
      },
      {
        path: 'live-stream/*',
        element: (
          <ProtectedRoute permission={['livestream_read']}>
            <LiveStream />
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    path: 'users/*',
    element: (
      <ProtectedRoute permission={['assign_user_channel_read']}>
        <ChannelUsers />
      </ProtectedRoute>
    ),
  },
  {
    path: 'settings/*',
    element: (
      <ProtectedRoute permission={['channel_setting_read', 'installation_default_read']}>
        <Settings />
      </ProtectedRoute>
    ),
  },
];

const router = createBrowserRouter(
  [
    {
      element: <Root />,
      children: [
        {
          path: '/login',
          element: <Login />,
        },
        {
          path: '/introduction-steps',
          element: <IntroductionSteps />,
        },
        {
          path: '/forgot-password',
          element: <ForgotPassword />,
        },
        {
          path: '/privacy-policy',
          element: <PrivacyPolicy />,
        },
        {
          path: '/terms-of-service',
          element: <TermsServices />,
        },

        {
          path: '/',
          element: (
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          ),
          children: [
            {
              path: 'profile',
              element: <ProfileLayer />,
              children: [
                {
                  index: true,
                  element: <Navigate to="profile-information" replace />,
                },
                {
                  path: 'profile-information',
                  element: <ProfileInformation />,
                },
                {
                  path: 'notification-preferences',
                  element: <NotificationPreferences />,
                },
                {
                  path: 'my-works',
                  element: <MyWorks />,
                },
              ],
            },

            {
              path: 'dashboard',
              element: <DashboardPanel />,
            },
            {
              path: 'performance-reporting',
              element: (
                <ProtectedRoute permission={['employee_read', 'performance_calculate']}>
                  <PerformanceReporting />
                </ProtectedRoute>
              ),
            },
            {
              path: 'users',
              element: (
                <ProtectedRoute permission={['user_read']}>
                  <Users />
                </ProtectedRoute>
              ),
            },
            {
              path: 'role-management',
              element: (
                <ProtectedRoute permission={['role_read']}>
                  <RoleManagement />
                </ProtectedRoute>
              ),
            },

            {
              path: 'notifications',
              element: <Notifications />,
            },

            {
              path: 'cms',
              element: (
                <ProtectedRoute permission={['cms_read']}>
                  <Cms />
                </ProtectedRoute>
              ),
            },

            {
              path: '',
              element: <Navigate to="/dashboard-panel" replace />,
            },
            {
              path: 'organization',
              element: (
                <ProtectedRoute permission={['organization_read']}>
                  <Organization />
                </ProtectedRoute>
              ),
            },

            {
              path: 'cms-management',
              element: <CmsManagement />,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoute permission={['channel_read']}>
                      <Navigate to="channels" replace />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'channels',
                  element: (
                    <ProtectedRoute permission={['channel_read']}>
                      <CmsChannels />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'policies',
                  element: (
                    <ProtectedRoute permission={['channel_read']}>
                      <CmsPolicies />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'settings',
                  element: <CmsSettings />,
                },
              ],
            },

            {
              path: 'mychannels',
              element: (
                <ProtectedRoute permission={['channel_read']}>
                  <MyChannels />
                </ProtectedRoute>
              ),
            },

            {
              path: 'cms-channels',
              element: <Channel />,
              children: getChannelChildRoutes(),
            },
            {
              path: 'my-channels',
              element: <Channel />,
              children: getChannelChildRoutes(),
            },

            {
              path: 'facebook/accounts',
              element: <FacebookCms />,
            },
            {
              path: 'facebook/my-pages',
              element: (
                <ProtectedRoute permission={['facebook_channel_read']}>
                  <FacebookPages />
                </ProtectedRoute>
              ),
            },
            {
              path: 'facebook/my-pages/detail',
              element: <FacebookPageDetail />,
              children: [
                {
                  index: true,
                  element: <Navigate to="contents/videos" replace />,
                },
                {
                  path: 'contents',
                  element: <FacebookPageContent />,
                  children: [
                    {
                      index: true,
                      element: <Navigate to="videos" replace />,
                    },
                    {
                      path: 'videos',
                      element: <FacebookPageVideos />,
                    },
                    {
                      path: 'expired-videos',
                      element: <FacebookPageExpiredVideos />,
                    },
                  ],
                },
                {
                  path: 'users',
                  element: <FacebookPageUsers />,
                },
                {
                  path: 'settings',
                  element: <FacebookPageSettings />,
                },
              ],
            },

            {
              path: 'reports',
              element: <ReportLayer />,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoute
                      permission={['user_report_read', 'portal_report_read', 'company_report_read']}
                    >
                      <ReportRedirect />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'user-reports',
                  element: (
                    <ProtectedRoute permission={['user_report_read']}>
                      <UserReports />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'portal-reports',
                  element: (
                    <ProtectedRoute permission={['portal_report_read']}>
                      <PortalReports />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'companyReports',
                  element: (
                    <ProtectedRoute permission={['company_report_read']}>
                      <CompanyReports />
                    </ProtectedRoute>
                  ),
                },
              ],
            },

            {
              path: 'dynamicParameter',
              element: <ParameterManagement />,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoute
                      permission={[
                        'product_read',
                        'company_read',
                        'licensor_read',
                        'producer_read',
                      ]}
                    >
                      <ParameterRedirect />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'productParameter',
                  element: (
                    <ProtectedRoute permission={['product_read']}>
                      <ProductParameter />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'companyParameter',
                  element: (
                    <ProtectedRoute permission={['company_read']}>
                      <CompanyParameter />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'licansorParameter',
                  element: (
                    <ProtectedRoute permission={['licensor_read']}>
                      <LicansorParameter />
                    </ProtectedRoute>
                  ),
                },
                {
                  path: 'producerParameter',
                  element: (
                    <ProtectedRoute permission={['producer_read']}>
                      <ProducerParameter />
                    </ProtectedRoute>
                  ),
                },
              ],
            },

            {
              path: 'documents',
              element: <Documents />,
            },

            {
              path: 'dashboard-panel',
              element: <DashboardPanel />,
            },

            {
              path: '*',
              element: <AuthenticatedNotFound />,
            },
            {
              path: '403',
              element: <Unauthorized />,
            },
          ],
        },

        {
          path: '*',
          element: <Navigate to="/login" replace />,
        },
        {
          path: '/info',
          element: <InfoComponent />,
        },
      ],
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
    },
  }
);

export default router;
